import React, { useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Save, Package, Car, Building, Home, Plane } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyService = {
  title_fr: '', title_en: '', title_ar: '',
  description_fr: '', description_en: '', description_ar: '',
  long_description_fr: '', long_description_en: '', long_description_ar: '',
  category: 'concierge',
  image_url: '',
  base_price: 0,
  currency: 'XAF',
  is_active: true,
  has_sub_services: false,
  sub_services: [],
};

const emptySubService = (type) => {
  const base = {
    type,
    title_fr: '', title_en: '', title_ar: '',
    description_fr: '', description_en: '', description_ar: '',
    base_price: 0,
  };
  if (type === 'vehicle_rental') return { ...base, title_fr: 'Location de Véhicules', vehicles: [] };
  if (type === 'venue_rental') return { ...base, title_fr: 'Location de Salles', venues: [] };
  if (type === 'accommodation') return { ...base, title_fr: 'Hébergement', accommodations: [] };
  if (type === 'airport_vip') return { ...base, title_fr: 'Accueil VIP Aéroport', services: [] };
  return base;
};

const subTypeLabels = {
  vehicle_rental: { label: 'Véhicules', icon: Car },
  venue_rental: { label: 'Salles', icon: Building },
  accommodation: { label: 'Hébergements', icon: Home },
  airport_vip: { label: 'VIP Aéroport', icon: Plane },
};

const ServiceManager = ({ services, token, onRefresh }) => {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => setEditing({ ...emptyService });
  const openEdit = (service) => setEditing({
    ...emptyService,
    ...service,
    sub_services: service.sub_services || [],
  });
  const close = () => setEditing(null);

  const saveService = async () => {
    if (!editing.title_fr || !editing.description_fr || !editing.image_url) {
      toast.error('Titre, description et image sont requis');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        base_price: parseFloat(editing.base_price) || 0,
      };
      if (editing.id) {
        await axios.put(`${API}/admin/services/${editing.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Service mis à jour');
      } else {
        await axios.post(`${API}/admin/services`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Service créé');
      }
      close();
      onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const deleteService = async (id) => {
    if (!window.confirm('Supprimer définitivement ce service ?')) return;
    try {
      await axios.delete(`${API}/admin/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Service supprimé');
      onRefresh();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Sub-services manipulation
  const addSubService = (type) => {
    setEditing({
      ...editing,
      sub_services: [...(editing.sub_services || []), emptySubService(type)],
    });
  };
  const updateSubService = (idx, patch) => {
    const list = [...editing.sub_services];
    list[idx] = { ...list[idx], ...patch };
    setEditing({ ...editing, sub_services: list });
  };
  const removeSubService = (idx) => {
    const list = [...editing.sub_services];
    list.splice(idx, 1);
    setEditing({ ...editing, sub_services: list });
  };

  // Items inside a sub-service (vehicles/venues/etc)
  const getItemsKey = (type) => ({
    vehicle_rental: 'vehicles',
    venue_rental: 'venues',
    accommodation: 'accommodations',
    airport_vip: 'services',
  }[type]);

  const addItem = (subIdx) => {
    const sub = editing.sub_services[subIdx];
    const key = getItemsKey(sub.type);
    const items = [...(sub[key] || [])];
    if (sub.type === 'vehicle_rental') {
      items.push({ name: '', images: [], passengers: 4, price: 0, features_fr: [], features_en: [], features_ar: [] });
    } else if (sub.type === 'venue_rental') {
      items.push({ name_fr: '', name_en: '', name_ar: '', type: 'wedding', capacity: 100, price: 0, images: [], features_fr: [], features_en: [], features_ar: [] });
    } else if (sub.type === 'accommodation') {
      items.push({ name_fr: '', name_en: '', name_ar: '', type: 'apartment', capacity: 2, price: 0, images: [], features_fr: [], features_en: [], features_ar: [] });
    } else if (sub.type === 'airport_vip') {
      items.push({ name_fr: '', name_en: '', name_ar: '', price: 0, includes_fr: [], includes_en: [], includes_ar: [] });
    }
    updateSubService(subIdx, { [key]: items });
  };

  const updateItem = (subIdx, itemIdx, patch) => {
    const sub = editing.sub_services[subIdx];
    const key = getItemsKey(sub.type);
    const items = [...(sub[key] || [])];
    items[itemIdx] = { ...items[itemIdx], ...patch };
    updateSubService(subIdx, { [key]: items });
  };

  const removeItem = (subIdx, itemIdx) => {
    const sub = editing.sub_services[subIdx];
    const key = getItemsKey(sub.type);
    const items = [...(sub[key] || [])];
    items.splice(itemIdx, 1);
    updateSubService(subIdx, { [key]: items });
  };

  const formatPrice = (p) => new Intl.NumberFormat('fr-FR').format(p || 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl text-text-primary">Gestion des services</h2>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white font-body text-sm hover:bg-primary/90 transition-colors"
          data-testid="new-service-btn"
        >
          <Plus className="w-4 h-4" />
          Nouveau service
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((service) => (
          <div key={service.id} className="bg-white p-4 border border-primary/10" data-testid={`admin-service-${service.id}`}>
            <img src={service.image_url} alt="" className="w-full h-32 object-cover mb-3" />
            <p className="overline text-secondary text-xs mb-1">{service.category?.toUpperCase()}</p>
            <h3 className="font-heading text-lg font-medium text-text-primary mb-1">
              {service.title_fr}
            </h3>
            <p className="font-body text-sm text-primary mb-2">{formatPrice(service.base_price)} XAF</p>
            {service.has_sub_services && (
              <p className="font-body text-xs text-secondary mb-3">
                <Package className="w-3 h-3 inline mr-1" />
                {service.sub_services?.length || 0} catégorie(s) · {
                  (service.sub_services || []).reduce((acc, s) => {
                    const k = getItemsKey(s.type);
                    return acc + ((s[k] || []).length);
                  }, 0)
                } item(s)
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => openEdit(service)}
                className="flex-1 px-3 py-2 text-sm font-body text-primary border border-primary/30 hover:bg-primary/5 transition-colors flex items-center justify-center gap-1"
                data-testid={`edit-service-${service.id}`}
              >
                <Pencil className="w-4 h-4" />
                Modifier
              </button>
              <button
                onClick={() => deleteService(service.id)}
                className="px-3 py-2 text-sm font-body text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
                data-testid={`delete-service-${service.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Editor Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-4xl my-8 shadow-xl" data-testid="service-editor-modal">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-primary/10 p-6 flex items-center justify-between z-10">
              <h2 className="font-heading text-2xl text-text-primary">
                {editing.id ? 'Modifier le service' : 'Nouveau service'}
              </h2>
              <button onClick={close} className="p-2 hover:bg-surface-light">
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Base info */}
              <section>
                <h3 className="font-heading text-lg text-text-primary mb-4">Informations de base</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <FieldText label="Titre FR" value={editing.title_fr} onChange={(v) => setEditing({ ...editing, title_fr: v })} testId="title-fr" />
                  <FieldText label="Titre EN" value={editing.title_en} onChange={(v) => setEditing({ ...editing, title_en: v })} testId="title-en" />
                  <FieldText label="Titre AR" value={editing.title_ar} onChange={(v) => setEditing({ ...editing, title_ar: v })} testId="title-ar" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <FieldTextarea label="Description FR" value={editing.description_fr} onChange={(v) => setEditing({ ...editing, description_fr: v })} testId="desc-fr" />
                  <FieldTextarea label="Description EN" value={editing.description_en} onChange={(v) => setEditing({ ...editing, description_en: v })} testId="desc-en" />
                  <FieldTextarea label="Description AR" value={editing.description_ar} onChange={(v) => setEditing({ ...editing, description_ar: v })} testId="desc-ar" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <div>
                    <label className="block text-xs text-text-muted mb-1">Catégorie</label>
                    <select
                      value={editing.category}
                      onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                      className="w-full border border-primary/20 p-2 text-sm font-body"
                    >
                      <option value="weddings">Mariages</option>
                      <option value="corporate">Corporate</option>
                      <option value="concierge">Conciergerie</option>
                      <option value="retreats">Retraites</option>
                      <option value="protocol">Protocole</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-text-muted mb-1">Prix de base (XAF)</label>
                    <input
                      type="number"
                      value={editing.base_price}
                      onChange={(e) => setEditing({ ...editing, base_price: e.target.value })}
                      className="w-full border border-primary/20 p-2 text-sm font-body"
                    />
                  </div>
                  <FieldText label="URL image" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} />
                </div>
              </section>

              {/* Sub-services toggle */}
              <section className="pt-4 border-t border-primary/10">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editing.has_sub_services}
                    onChange={(e) => setEditing({ ...editing, has_sub_services: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="font-body text-sm text-text-primary">
                    Ce service a des sous-services (catalogue : véhicules, salles, hébergements, VIP)
                  </span>
                </label>
              </section>

              {/* Sub-services editor */}
              {editing.has_sub_services && (
                <section className="pt-4 border-t border-primary/10">
                  <h3 className="font-heading text-lg text-text-primary mb-4">Catalogue de sous-services</h3>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {Object.entries(subTypeLabels).map(([type, cfg]) => {
                      const Icon = cfg.icon;
                      return (
                        <button
                          key={type}
                          onClick={() => addSubService(type)}
                          className="flex items-center gap-2 px-3 py-2 text-sm font-body text-primary border border-primary/30 hover:bg-primary/5"
                          data-testid={`add-sub-${type}`}
                        >
                          <Icon className="w-4 h-4" />
                          + {cfg.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="space-y-4">
                    {(editing.sub_services || []).map((sub, idx) => (
                      <SubServiceBlock
                        key={idx}
                        sub={sub}
                        idx={idx}
                        onUpdate={(patch) => updateSubService(idx, patch)}
                        onRemove={() => removeSubService(idx)}
                        onAddItem={() => addItem(idx)}
                        onUpdateItem={(i, p) => updateItem(idx, i, p)}
                        onRemoveItem={(i) => removeItem(idx, i)}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-primary/10 p-4 flex gap-3 justify-end">
              <button onClick={close} className="btn-outline px-6">Annuler</button>
              <button
                onClick={saveService}
                disabled={saving}
                className="btn-primary px-6 flex items-center gap-2 disabled:opacity-50"
                data-testid="save-service-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== SUB COMPONENTS ==============

const FieldText = ({ label, value, onChange, testId }) => (
  <div>
    <label className="block text-xs text-text-muted mb-1">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-primary/20 p-2 text-sm font-body"
      data-testid={testId}
    />
  </div>
);

const FieldTextarea = ({ label, value, onChange, testId }) => (
  <div>
    <label className="block text-xs text-text-muted mb-1">{label}</label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      rows={2}
      className="w-full border border-primary/20 p-2 text-sm font-body resize-none"
      data-testid={testId}
    />
  </div>
);

const SubServiceBlock = ({ sub, idx, onUpdate, onRemove, onAddItem, onUpdateItem, onRemoveItem }) => {
  const cfg = subTypeLabels[sub.type] || { label: sub.type, icon: Package };
  const Icon = cfg.icon;
  const itemsKey = { vehicle_rental: 'vehicles', venue_rental: 'venues', accommodation: 'accommodations', airport_vip: 'services' }[sub.type];
  const items = sub[itemsKey] || [];

  return (
    <div className="border border-primary/20 bg-surface-light/50" data-testid={`sub-service-${idx}`}>
      <div className="flex items-center justify-between p-3 bg-primary/5 border-b border-primary/10">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" />
          <span className="font-body text-sm font-medium text-text-primary">{cfg.label}</span>
          <span className="text-xs text-text-muted">({items.length} item{items.length > 1 ? 's' : ''})</span>
        </div>
        <button onClick={onRemove} className="text-red-500 hover:bg-red-50 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <FieldText label="Titre FR" value={sub.title_fr} onChange={(v) => onUpdate({ title_fr: v })} />
          <FieldText label="Titre EN" value={sub.title_en} onChange={(v) => onUpdate({ title_en: v })} />
          <FieldText label="Titre AR" value={sub.title_ar} onChange={(v) => onUpdate({ title_ar: v })} />
        </div>
        <FieldTextarea label="Description FR" value={sub.description_fr} onChange={(v) => onUpdate({ description_fr: v })} />

        {/* Items list */}
        <div className="pt-3 border-t border-primary/10">
          <div className="flex items-center justify-between mb-3">
            <p className="font-body text-sm font-medium text-text-primary">Catalogue détaillé</p>
            <button
              onClick={onAddItem}
              className="flex items-center gap-1 px-2 py-1 text-xs font-body text-primary border border-primary/30 hover:bg-primary/5"
              data-testid={`add-item-${idx}`}
            >
              <Plus className="w-3 h-3" />
              Ajouter
            </button>
          </div>

          <div className="space-y-2">
            {items.map((item, i) => (
              <CatalogItem
                key={i}
                type={sub.type}
                item={item}
                onUpdate={(p) => onUpdateItem(i, p)}
                onRemove={() => onRemoveItem(i)}
              />
            ))}
            {items.length === 0 && (
              <p className="text-xs text-text-muted italic">Aucun item. Cliquez sur "Ajouter" pour en créer.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CatalogItem = ({ type, item, onUpdate, onRemove }) => {
  const featuresKey = type === 'airport_vip' ? 'includes_fr' : 'features_fr';
  const featuresText = (item[featuresKey] || []).join('\n');
  const imagesText = (item.images || []).join('\n');

  const updateFeatures = (text) => {
    const arr = text.split('\n').map((s) => s.trim()).filter(Boolean);
    onUpdate({ [featuresKey]: arr });
  };

  const updateImages = (text) => {
    const arr = text.split('\n').map((s) => s.trim()).filter(Boolean);
    onUpdate({ images: arr });
  };

  return (
    <div className="bg-white border border-primary/10 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="font-body text-xs text-text-muted">
          {type === 'vehicle_rental' && (item.name || 'Nouveau véhicule')}
          {type === 'venue_rental' && (item.name_fr || 'Nouvelle salle')}
          {type === 'accommodation' && (item.name_fr || 'Nouveau logement')}
          {type === 'airport_vip' && (item.name_fr || 'Nouvelle formule')}
        </p>
        <button onClick={onRemove} className="text-red-500 hover:bg-red-50 p-1">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {type === 'vehicle_rental' && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <FieldText label="Nom (marque/modèle)" value={item.name} onChange={(v) => onUpdate({ name: v })} />
          <div>
            <label className="block text-xs text-text-muted mb-1">Passagers</label>
            <input type="number" value={item.passengers || 0} onChange={(e) => onUpdate({ passengers: parseInt(e.target.value) || 0 })} className="w-full border border-primary/20 p-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1">Prix/jour (XAF)</label>
            <input type="number" value={item.price || 0} onChange={(e) => onUpdate({ price: parseFloat(e.target.value) || 0 })} className="w-full border border-primary/20 p-2 text-sm" />
          </div>
        </div>
      )}

      {type === 'venue_rental' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <FieldText label="Nom FR" value={item.name_fr} onChange={(v) => onUpdate({ name_fr: v })} />
            <FieldText label="Nom EN" value={item.name_en} onChange={(v) => onUpdate({ name_en: v })} />
            <FieldText label="Nom AR" value={item.name_ar} onChange={(v) => onUpdate({ name_ar: v })} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs text-text-muted mb-1">Type</label>
              <select value={item.type || 'wedding'} onChange={(e) => onUpdate({ type: e.target.value })} className="w-full border border-primary/20 p-2 text-sm">
                <option value="wedding">Mariage</option>
                <option value="meeting">Réunion</option>
                <option value="concert">Concert</option>
                <option value="conference">Conférence</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Capacité</label>
              <input type="number" value={item.capacity || 0} onChange={(e) => onUpdate({ capacity: parseInt(e.target.value) || 0 })} className="w-full border border-primary/20 p-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Prix/jour (XAF)</label>
              <input type="number" value={item.price || 0} onChange={(e) => onUpdate({ price: parseFloat(e.target.value) || 0 })} className="w-full border border-primary/20 p-2 text-sm" />
            </div>
          </div>
        </>
      )}

      {type === 'accommodation' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <FieldText label="Nom FR" value={item.name_fr} onChange={(v) => onUpdate({ name_fr: v })} />
            <FieldText label="Nom EN" value={item.name_en} onChange={(v) => onUpdate({ name_en: v })} />
            <FieldText label="Nom AR" value={item.name_ar} onChange={(v) => onUpdate({ name_ar: v })} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs text-text-muted mb-1">Type</label>
              <select value={item.type || 'apartment'} onChange={(e) => onUpdate({ type: e.target.value })} className="w-full border border-primary/20 p-2 text-sm">
                <option value="apartment">Appartement</option>
                <option value="villa">Villa</option>
                <option value="hotel_suite">Suite hôtelière</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Capacité</label>
              <input type="number" value={item.capacity || 0} onChange={(e) => onUpdate({ capacity: parseInt(e.target.value) || 0 })} className="w-full border border-primary/20 p-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Prix/nuit (XAF)</label>
              <input type="number" value={item.price || 0} onChange={(e) => onUpdate({ price: parseFloat(e.target.value) || 0 })} className="w-full border border-primary/20 p-2 text-sm" />
            </div>
          </div>
        </>
      )}

      {type === 'airport_vip' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <FieldText label="Nom formule FR" value={item.name_fr} onChange={(v) => onUpdate({ name_fr: v })} />
          <FieldText label="Nom EN" value={item.name_en} onChange={(v) => onUpdate({ name_en: v })} />
          <FieldText label="Nom AR" value={item.name_ar} onChange={(v) => onUpdate({ name_ar: v })} />
          <div>
            <label className="block text-xs text-text-muted mb-1">Prix (XAF)</label>
            <input type="number" value={item.price || 0} onChange={(e) => onUpdate({ price: parseFloat(e.target.value) || 0 })} className="w-full border border-primary/20 p-2 text-sm" />
          </div>
        </div>
      )}

      {/* Images gallery (not for airport_vip which has no visual) */}
      {type !== 'airport_vip' && (
        <div>
          <label className="block text-xs text-text-muted mb-1">
            Images (une URL par ligne) · {(item.images || []).length} image(s)
          </label>
          <textarea
            value={imagesText}
            onChange={(e) => updateImages(e.target.value)}
            rows={3}
            placeholder="https://images.unsplash.com/photo-xxx?w=900&#10;https://images.unsplash.com/photo-yyy?w=900"
            className="w-full border border-primary/20 p-2 text-xs font-mono resize-none"
          />
          {(item.images || []).length > 0 && (
            <div className="flex gap-2 mt-2 overflow-x-auto">
              {item.images.map((url, i) => (
                <img key={i} src={url} alt="" className="w-16 h-16 object-cover flex-shrink-0 border border-primary/10" />
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-xs text-text-muted mb-1">
          {type === 'airport_vip' ? 'Inclus FR (une ligne par item)' : 'Caractéristiques FR (une ligne par item)'}
        </label>
        <textarea
          value={featuresText}
          onChange={(e) => updateFeatures(e.target.value)}
          rows={3}
          placeholder={type === 'vehicle_rental' ? 'Chauffeur professionnel\nClimatisation\nWiFi' : 'Piscine\nJardin privé\nTerrasse'}
          className="w-full border border-primary/20 p-2 text-sm font-body resize-none"
        />
      </div>
    </div>
  );
};

export default ServiceManager;
